import React from "react";
import styles from "./Statement.module.css";

/*
 * Statement — the mixed sans / serif headline. THE signature of the design.
 *
 *   <Statement as="h1" size="statement" align="center">
 *     BUILDING SOFTWARE THAT <em>SHIPS</em>.
 *   </Statement>
 *
 * The whole line sets in Mona Sans 800, condensed (font-stretch
 * var(--wdth-display)), uppercase, line-height .92. Every <em> child sets in
 * Instrument Serif regular — NOT italic — at the same cap height, on the same
 * baseline, coloured var(--section-accent): --lime-ink on olive / void, --ink
 * on paper. <em> is plain JSX; it is styled by a descendant selector, so there
 * is no dangerouslySetInnerHTML and no second component to import.
 *
 * Props
 *   as         "h1" | "h2" | "h3" | "p"            element (default "h2")
 *   size       "statement" | "h2" | "h3"           type-scale token. Defaults to
 *              match `as`: h2 -> "h2", h3 -> "h3", anything else -> "statement".
 *   align      "left" | "center"                   default "left". "center" also
 *              centres the block and clamps its measure so a statement breaks
 *              into 5-7 lines at 1440 like the reference manifesto.
 *   className  extra class (margins, a custom max-width)
 *   ...rest    forwarded to the element (id, aria-*, data-*, style)
 *
 * Tunable custom properties (set from a className or style — never needed for
 * the default look):
 *   --statement-size     overrides the size token
 *   --statement-color    overrides the text colour   (default --section-text)
 *   --statement-accent   overrides the <em> colour   (default --section-accent)
 *   --statement-measure  overrides the centred max-width
 *
 * Motion: none here, on purpose. HeadingWipe (App.js) clip-wipes every
 * "main h2" itself — never animate a Statement h2 from a section.
 */

const SIZE_CLASS = {
  statement: styles.sizeStatement,
  h2: styles.sizeH2,
  h3: styles.sizeH3
};

const DEFAULT_SIZE_FOR_TAG = {
  h2: "h2",
  h3: "h3"
};

function cx(...values) {
  return values.filter(Boolean).join(" ");
}

// forwardRef so <Reveal as={Statement}> (an h1 / h3 / p — never an h2, those
// belong to HeadingWipe) can observe the element. Without it Reveal's ref stays
// null and the statement is left at opacity 0 for good.
const Statement = React.forwardRef(function Statement(
  { as: Tag = "h2", size, align = "left", className, children, ...rest },
  ref
) {
  const resolvedSize = SIZE_CLASS[size]
    ? size
    : DEFAULT_SIZE_FOR_TAG[Tag] || "statement";

  return (
    <Tag
      ref={ref}
      className={cx(
        styles.root,
        SIZE_CLASS[resolvedSize],
        align === "center" && styles.center,
        className
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
});

export default Statement;
